import { ArrowDown, Cog, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { IMM } from "@/data/content";

export const IMMSection = () => {
  const scrollToAudit = (e) => {
    e.preventDefault();
    const el = document.getElementById("visibility-audit");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section id="imm" data-testid="imm-section" className="bg-white py-16 md:py-24 scroll-mt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto">
          <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">
            {IMM.eyebrow}
          </p>
          <h2 className="mt-4 font-nunito text-3xl sm:text-4xl md:text-[2.75rem] font-extrabold text-[#383838] leading-tight">
            {IMM.heading}
          </h2>
          <p className="mt-5 text-sm md:text-base text-[#6e6e6e] leading-relaxed">{IMM.sub}</p>
        </div>

        {/* Features + Machine visual */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-8">
            {IMM.features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex items-start gap-4">
                  <div className="shrink-0 h-12 w-12 rounded-[12px] bg-[#ee2e24]/[0.08] border border-[#ee2e24]/20 flex items-center justify-center">
                    <Icon size={21} strokeWidth={1.75} className="text-[#ee2e24]" />
                  </div>
                  <div>
                    <h4 className="font-nunito text-base font-extrabold text-[#383838]">{f.title}</h4>
                    <p className="mt-1.5 text-sm text-[#6e6e6e] leading-relaxed">{f.body}</p>
                  </div>
                </div>
              );
            })}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a
                href="#visibility-audit"
                onClick={scrollToAudit}
                data-testid="imm-primary-cta-button"
                className="inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.3)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150"
              >
                Get My Free Visibility Audit
              </a>
            </div>
          </div>

          {/* The Machine — CSS-built diagram */}
          <div data-testid="imm-machine-visual" className="rounded-[16px] border border-[#ececec] bg-[#fafafa] p-6 sm:p-8 shadow-[0_14px_40px_rgba(2,2,2,0.08)]">
            <div className="grid grid-cols-2 gap-3.5">
              {IMM.engines.map((e) => {
                const Icon = e.icon;
                return (
                  <div
                    key={e.label}
                    className="rounded-[12px] bg-white border border-[#ececec] px-4 py-4 flex items-center gap-3 shadow-[0_6px_18px_rgba(2,2,2,0.05)] hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(2,2,2,0.1)] transition-transform duration-200"
                  >
                    <Icon size={19} strokeWidth={1.75} className="text-[#ee2e24] shrink-0" />
                    <span className="font-nunito text-[12px] font-extrabold uppercase tracking-[0.08em] text-[#383838]">
                      {e.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center py-3">
              <ArrowDown size={18} strokeWidth={1.75} className="text-[#c9c9c9]" />
            </div>

            <div className="rounded-[12px] bg-[#0b0b0c] px-5 py-4 flex items-center justify-center gap-3">
              <Cog size={20} strokeWidth={1.75} className="text-[#ee2e24] imm-spin" />
              <span className="font-nunito text-[13px] font-extrabold uppercase tracking-[0.2em] text-white">
                Automation Core
              </span>
            </div>

            <div className="flex justify-center py-3">
              <ArrowDown size={18} strokeWidth={1.75} className="text-[#c9c9c9]" />
            </div>

            <div className="rounded-[12px] border border-[#ee2e24]/25 bg-[#ee2e24]/[0.06] px-5 py-4 flex items-center justify-center gap-3">
              <TrendingUp size={19} strokeWidth={1.75} className="text-[#ee2e24]" />
              <span className="font-nunito text-[13px] font-extrabold uppercase tracking-[0.14em] text-[#383838]">
                Traffic · Leads · Sales
              </span>
            </div>
          </div>
        </div>

        {/* Industry flavors */}
        <div id="industries" className="mt-16 scroll-mt-28">
          <p className="text-center font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#9a9a9a]">
            Tuned for your industry
          </p>
          <div className="mt-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {IMM.industries.map((ind) => {
              const Icon = ind.icon;
              const cardClass =
                "block rounded-[12px] border border-[#ececec] bg-white px-4 py-5 text-center hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(2,2,2,0.1)] hover:border-[#ee2e24]/30 transition-transform duration-200 shadow-[0_6px_18px_rgba(2,2,2,0.04)]";
              const inner = (
                <>
                  <div className="mx-auto h-11 w-11 rounded-full bg-[#ee2e24]/[0.08] flex items-center justify-center">
                    <Icon size={19} strokeWidth={1.75} className="text-[#ee2e24]" />
                  </div>
                  <h5 className="mt-3 font-nunito text-[13px] font-extrabold text-[#383838]">{ind.name}</h5>
                  <p className="mt-1.5 text-[11.5px] text-[#8a8a8a] leading-snug">{ind.line}</p>
                </>
              );
              return ind.href ? (
                <Link key={ind.key} to={ind.href} data-testid={ind.testId} className={cardClass}>
                  {inner}
                </Link>
              ) : (
                <div key={ind.key} data-testid={ind.testId} className={cardClass}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
